// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Components
import TooltipHelper from '../tooltip/TooltipHelper';
import Entity from '../entity/Entity';
import { Button } from '@/common-utils/components/ui/button';
import { Input } from '../../ui/input';
// Style
import './TabularList.scss';

/**
 * @param root
 * @param root.data
 * @param root.tabularRows
 * @param root.filters
 * @param root.showResultsNumber
 * @param root.cellClassName
 * @param root.emptyResults
 *@returns {JSX} AllowedClientsTable
 */
function TabularList({
    data,
    tabularRows,
    filters = [],
    showResultsNumber = true,
    cellClassName,
    emptyResults = { entity: 'table.no_results', namespace: '' }
}) {

    // Constants
    const { items: initialItems } = data || {};

    // State
    const [listData, setListData] = useState(initialItems);

    // Hooks
    useEffect(() => {
        setListData(initialItems);
    }, [initialItems]);

    // Functions
    // Helper function to format data based on type
    const formatValue = (row, value = '') => {
        switch (row.type) {
            case 'text':
                {
                    return (
                        <Entity
                            entity={`${row.prefix ?? ''}${row.key ? value[row.key] : value}`}
                            namespace={row.namespace}
                        />
                    );

                }
            case 'button':
                {
                    return (
                        <TooltipHelper
                            asChild
                            trigger={
                                <Button
                                    onClick={() => row.action(value)}
                                    icon={row.icon}
                                    entity={row.entity}
                                    namespace={row.namespace}
                                    variant={row.variant}
                                    className={cn('mr-2 rounded-full', row.entity ? 'h-10' : '')}
                                    disabled={!row.isAllowed}
                                />
                            }
                            content={<Entity entity={row.title} namespace={row.namespace} />}
                        />
                    );

                }
            default:
                return <div>{value}</div>;
        }
    };

    // Custom filter
    const filterList = (e, filterKey) => {
        const filterValue = e.target.value;
        const filteredData = (initialItems || []).filter((item) => item[filterKey].includes(filterValue));
        if (filterValue) {
            setListData(filteredData);
        }
        else {
            setListData(initialItems);
        }
    };

    return (
        <div className='tabular-list-container w-full'>
            <div className='tabular-list-header'>
                {filters.length > 0 &&
                    filters.map((filter) => (
                        <Input
                            key={filter.entity}
                            className='sm:w-96'
                            type='text'
                            entity={filter.entity}
                            namespace={filter.namespace}
                            onChange={(e) => filterList(e, filter.key)}
                        />
                    ))
                }
                {showResultsNumber &&
                    <div className='flex-2 text-sm text-muted-foreground'>
                        <Entity entity={'table.results'} data={{ results: listData.length }} />
                    </div>}
            </div>
            {
                !listData.length &&
                <div className='tabular-list-row'>
                    <div className='tabular-list-cell h-24 text-center'>
                        <Entity entity={emptyResults.entity} namespace={emptyResults.namespace} />
                    </div>
                </div>
            }
            {
                listData.length > 0 &&
                <div className='tabular-scroll-wrapper mt-4'>
                    {listData?.map((item, index) => (
                        <div key={`${item}-${index}`} className='tabular-list-row'>
                            {tabularRows.map((row, indx) => (
                                <div className={cn('tabular-list-cell', cellClassName)} key={`${item}-${indx}`}>
                                    {formatValue(row, item)}
                                </div>
                            ))
                            }
                        </div>
                    ))}
                </div>
            }
        </div>
    );
}

TabularList.propTypes = {
    data: PropTypes.shape({
        items: PropTypes.array
    }).isRequired,
    tabularRows: PropTypes.array.isRequired,
    filters: PropTypes.array,
    showResultsNumber: PropTypes.bool,
    cellClassName: PropTypes.string,
    emptyResults: PropTypes.shape({
        entity: PropTypes.string,
        namespace: PropTypes.string
    })
};

export default TabularList;